import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, Beaker, Camera, CheckCircle2, ChevronDown, Droplets,
  Loader2, MapPin, Trash2, User, Users,
} from "lucide-react";
import api, { formatApiError as errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const REC_ORDER = ["no_immediate_action", "tank_clean", "biocleanse", "filtration_service",
  "filtration_upgrade", "uv_recommended", "uv_service",
  "aware_lab_test", "plumbing_pump", "other"];
const FIELD_LABELS = {
  "tank.sediment": "visible sediment", "tank.water_condition": "tank water condition",
  "tank.lid_access": "tank access / lid", "tank.overflow_screening": "overflow / screening",
  "catchment.roof_gutter": "roof / gutter condition", "catchment.first_flush": "first flush diverter",
  "catchment.risks": "contamination risks", "treatment.filtration_installed": "filtration installed",
  "treatment.filtration_type": "filtration system type", "treatment.filter_condition": "filter condition",
  "treatment.uv_installed": "UV installed", "treatment.uv_condition": "UV condition",
  "owner.owner_present": "owner present", "outcome.recommendations": "recommendations",
  "outcome.priority": "priority", "outcome.summary": "technician summary",
  "outcome.other_note": "note for 'Other'",
};

/** Server detail can be a string or a structured 422 — always show
    the technician something they can act on. */
const msg = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (d.error === "incomplete") {
      return `Still needed: ${(d.missing || []).map((k) => FIELD_LABELS[k] || k).join(", ")}`;
    }
    if (d.error === "cover_confirmation_required") return d.message || "Confirmation needed to continue";
    if (d.detail) return d.detail;
  }
  return errText(e);
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
// How many tanks of each type — [schema key, saved label]
const TANK_TYPES = [["poly", "Poly"], ["concrete", "Concrete"], ["steel", "Steel"],
  ["other", "Other"]];

const Chips = ({ label, options, value, onPick, testid, multi = false }) => {
  const on = (o) => (multi ? (value || []).includes(o) : value === o);
  return (
    <div className="mt-4">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" data-testid={`${testid}-${slug(o)}`} onClick={() => onPick(o)}
            className={`rounded-full px-4 py-3 text-sm font-bold transition-colors ${on(o)
              ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-700 active:bg-slate-200"}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
};

const Notes = ({ label, value, onChange, testid, rows = 2, placeholder = "Optional short notes" }) => (
  <div className="mt-4">
    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div>
    <textarea data-testid={testid} rows={rows} value={value || ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-base" />
  </div>
);

const Section = ({ title, icon: Icon, children, testid, open, onToggle, done }) => (
  <div className="mt-4 rounded-2xl border border-slate-200 bg-white" data-testid={testid}>
    <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`}
      className="flex w-full items-center gap-3 p-4 text-left">
      <Icon className={`h-5 w-5 shrink-0 ${done ? "text-emerald-600" : "text-cyan-700"}`} />
      <span className="flex-1 text-base font-extrabold text-slate-900">{title}</span>
      {done && <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />}
      <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="border-t border-slate-100 px-4 pb-5">{children}</div>}
  </div>
);

export default function FieldHealthCheck() {
  const { reference } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [sections, setSections] = useState({});
  const [err, setErr] = useState("");
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const [missing, setMissing] = useState([]);
  const [open, setOpen] = useState({ tank: true });
  const [busy, setBusy] = useState(false);
  const [unableOpen, setUnableOpen] = useState(false);
  const [unableReason, setUnableReason] = useState("");
  const [unableNotes, setUnableNotes] = useState("");
  const [coverDialog, setCoverDialog] = useState(null);
  const [cancelledCover, setCancelledCover] = useState(false);
  const timer = useRef(null);
  const pending = useRef({});
  const coverPrompt = useRef(null);

  // Nov 2026 — any active, authorised AWARE technician may cover a job
  // that's assigned to someone else (or unassigned) once they confirm who's
  // who. Confirmed once per job per session — every call for this reference
  // then carries it automatically, without re-prompting on every autosave.
  const coverKey = `hc_cover_${reference}`;
  const coverParams = () =>
    (sessionStorage.getItem(coverKey) === "1" ? { confirm_cover: true } : {});
  const withCoverRetry = useCallback(async (callFactory) => {
    try {
      return await callFactory();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 422 && d && typeof d === "object"
        && d.error === "cover_confirmation_required") {
        // Share ONE prompt between concurrent 422s (StrictMode double effect,
        // autosave racing a load) so no caller is left awaiting a resolver
        // that was overwritten.
        if (!coverPrompt.current) {
          coverPrompt.current = new Promise((resolve) => setCoverDialog({ ...d, onDecide: resolve }))
            .finally(() => { coverPrompt.current = null; setCoverDialog(null); });
        }
        const proceed = await coverPrompt.current;
        if (!proceed) throw e;
        sessionStorage.setItem(coverKey, "1");
        return await callFactory();
      }
      throw e;
    }
  }, [coverKey]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await withCoverRetry(() =>
        api.get(`/field/health-checks/${reference}`, { params: coverParams() }));
      setData(d); setSections(d.form?.sections || {}); setErr(""); setCancelledCover(false);
    } catch (e) {
      setErr(msg(e)); setDenied(e?.response?.status === 403);
      setCancelledCover(e?.response?.status === 422);
    }
  }, [reference]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  // Sep 2026 — a refusal that NAMES the technician means the phone is signed
  // in as a different account (old session, or a second account with the same
  // name). Clearing the session and re-entering the PIN fixes it on the spot.
  const signInAgain = async () => {
    try { await logout(); } catch { /* sign-out must never block */ }
    window.location.href = `/field/login?next=${encodeURIComponent(`/field/health-check/${reference}`)}`;
  };

  const completed = data?.form?.status === "completed";
  // Debounced autosave. Every touched section is accumulated so rapid
  // chip taps across sections all reach the server in the one flush —
  // the debounce must never drop an earlier section's answer.
  const queueSave = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const body = pending.current;
      pending.current = {};
      setSaving(true);
      try {
        const { data: r } = await withCoverRetry(() => api.put(
          `/field/health-checks/${reference}/form`, { sections: body }, { params: coverParams() }));
        setSavedAt(r.saved_at); setMissing(r.missing || []);
      } catch (e) {
        pending.current = { ...body, ...pending.current };
        toast.error(msg(e));
      }
      finally { setSaving(false); }
    }, 600);
  }, [reference]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (sec, key, val) => {
    if (completed) return;
    setSections((prev) => {
      const next = { ...prev, [sec]: { ...(prev[sec] || {}), [key]: val } };
      queueSave({ [sec]: next[sec] });
      return next;
    });
  };
  const val = (sec, key) => (sections[sec] || {})[key];
  const setMany = (sec, patch) => {
    if (completed) return;
    setSections((prev) => {
      const next = { ...prev, [sec]: { ...(prev[sec] || {}), ...patch } };
      queueSave({ [sec]: next[sec] });
      return next;
    });
  };
  const toggleMulti = (sec, key, opt) => {
    const cur = val(sec, key) || [];
    set(sec, key, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };
  // "No immediate action" can never sit beside a recommendation — tapping
  // one clears the other rather than failing the save later.
  const toggleRec = (slugKey) => {
    const cur = val("outcome", "recommendations") || [];
    let next;
    if (cur.includes(slugKey)) next = cur.filter((x) => x !== slugKey);
    else if (slugKey === "no_immediate_action") next = ["no_immediate_action"];
    else next = [...cur.filter((x) => x !== "no_immediate_action"), slugKey];
    set("outcome", "recommendations", next);
  };

  const upload = async (file, category) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category);
    try {
      await withCoverRetry(() => api.post(`/field/health-checks/${reference}/photos`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, params: coverParams() }));
      await load(); toast.success("Photo added");
    } catch (e) { toast.error(msg(e)); }
    finally { setBusy(false); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await withCoverRetry(fn); await load(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(msg(e)); }
    finally { setBusy(false); }
  };

  // Rendered from every branch (incl. the initial loading spinner — the
  // very first GET is what raises cover_confirmation_required).
  const coverModal = coverDialog && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          data-testid="field-hc-cover-dialog">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5">
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6 text-amber-600" />
              <p className="text-base font-extrabold text-slate-900">
                {coverDialog.unassigned ? "Unassigned job" : "Assigned to someone else"}
              </p>
            </div>
            <p className="mt-2 text-sm text-slate-700">{coverDialog.message}</p>
            <p className="mt-2 text-xs text-slate-500">
              The AWARE/ServiceM8 assignment won&apos;t change — this is just so the office
              knows who actually did the work.
            </p>
            <div className="mt-4 flex gap-2">
              <button data-testid="field-hc-cover-cancel"
                onClick={() => coverDialog.onDecide(false)}
                className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-bold text-slate-600">
                CANCEL
              </button>
              <button data-testid="field-hc-cover-confirm"
                onClick={() => coverDialog.onDecide(true)}
                className="flex-1 rounded-full bg-cyan-700 py-3 text-sm font-extrabold text-white">
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      );

  if (err) return (
    <div className="p-8 text-center" data-testid="field-hc-error">
      <AlertTriangle className="mx-auto h-8 w-8 text-rose-600" />
      <p className="mt-3 text-base font-bold text-rose-700">{err}</p>
      {denied && (
        <button onClick={signInAgain} data-testid="field-hc-sign-in-again"
          className="mx-auto mt-4 block rounded-full bg-cyan-700 px-5 py-3 text-sm font-bold text-white">
          NOT YOU? SIGN IN AGAIN
        </button>
      )}
      {cancelledCover && (
        <button onClick={load} data-testid="field-hc-continue-anyway"
          className="mx-auto mt-4 block rounded-full bg-cyan-700 px-5 py-3 text-sm font-bold text-white">
          CONTINUE ANYWAY
        </button>
      )}
      <div className="mx-auto mt-4 max-w-sm rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left">
        <p className="text-xs font-bold text-amber-900">Need this sorted now?</p>
        <p className="mt-1 text-xs text-amber-800">
          Call the AWARE office — they can reassign the job while you wait, then
          just reopen the link from your text message.
        </p>
        <a href="tel:+61752388182" data-testid="field-hc-call-office"
          className="mt-2 inline-block text-xs font-bold text-amber-900 underline">
          07 5238 8182
        </a>
      </div>
      <button onClick={() => navigate("/field/today")} className="mt-4 rounded-full bg-slate-900 px-5 py-3 text-sm font-bold text-white">
        BACK TO TODAY
      </button>
      {coverModal}
    </div>
  );
  if (!data) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" />{coverModal}</div>;

  const h = data.header;
  const S = data.schema.single;
  const M = data.schema.multi;
  const recs = val("outcome", "recommendations") || [];
  const done = (keys) => keys.every((k) => !missing.includes(k));

  const Photos = ({ category, label }) => {
    const rows = (data.photos || []).filter((p) => p.category === category);
    return (
      <div className="mt-4">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div>
        {!completed && !!rows.length && (
          <div className="mt-1 text-[11px] text-slate-500">
            Photos go in the customer&apos;s report by default — tap the label to keep one
            internal. Add a short caption so it reads properly in the report.
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {rows.map((p) => (
            <div key={p.id} className="relative w-[104px]" data-testid={`field-hc-photo-${p.id}`}>
              <AuthImage path={`/field/health-checks/${reference}/photos/${p.id}`}
                alt={p.caption || p.category} className="h-16 w-full rounded-lg object-cover"
                testId={`field-hc-photo-img-${p.id}`} />
              {!completed && (
                <button data-testid={`field-hc-photo-del-${p.id}`} disabled={busy}
                  onClick={() => act(() => api.delete(`/field/health-checks/${reference}/photos/${p.id}`,
                    { params: coverParams() }), "Photo removed")}
                  className="absolute -right-1 -top-1 rounded-full bg-rose-600 p-1 text-white">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
              {!completed ? (
                <input data-testid={`field-hc-photo-caption-${p.id}`}
                  defaultValue={p.caption || ""} placeholder="Add a caption…" maxLength={120}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v === (p.caption || "")) return;
                    act(() => api.patch(
                      `/field/health-checks/${reference}/photos/${p.id}/caption`,
                      { caption: v }, { params: coverParams() }), v ? "Caption saved" : "Caption cleared");
                  }}
                  className="mt-1 w-full rounded-md border border-slate-300 px-1.5 py-1 text-[10px]" />
              ) : p.caption ? (
                <div className="mt-1 text-[10px] font-semibold text-slate-600">{p.caption}</div>
              ) : null}
              {!completed && (
                <button data-testid={`field-hc-photo-del-${p.id}`} disabled={busy}
                  onClick={() => act(() => api.delete(`/field/health-checks/${reference}/photos/${p.id}`,
                    { params: coverParams() }), "Photo removed")}
                  className="absolute -right-1 -top-1 rounded-full bg-rose-600 p-1 text-white">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
              <button data-testid={`field-hc-photo-visibility-${p.id}`}
                disabled={busy || completed}
                onClick={() => act(() => api.patch(
                  `/field/health-checks/${reference}/photos/${p.id}/visibility`,
                  { customer_visible: !p.customer_visible }, { params: coverParams() }),
                  p.customer_visible ? "Photo set to internal only" : "Photo added to the report")}
                className={`mt-1 w-full rounded-full px-1.5 py-1 text-[9px] font-extrabold uppercase
                  ${p.customer_visible
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-200 text-slate-600"} disabled:opacity-60`}>
                {p.customer_visible ? "In report" : "Internal only"}
              </button>
            </div>
          ))}
          {!completed && (
            <label data-testid={`field-hc-photo-add-${category}`}
              className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-500">
              <Camera className="h-5 w-5" />
              <span className="text-[10px] font-bold">ADD</span>
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => upload(e.target.files?.[0], category)} />
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pb-40 pt-4" data-testid="field-health-check-page">
      <button onClick={() => navigate("/field/today")} data-testid="field-hc-back"
        className="flex items-center gap-2 text-sm font-bold text-slate-500">
        <ArrowLeft className="h-4 w-4" /> TODAY
      </button>

      {/* Known from the booking — never retyped */}
      <div className="mt-3 rounded-2xl bg-slate-900 p-4 text-white" data-testid="field-hc-header">
        <div className="text-xs font-bold uppercase tracking-widest text-cyan-300">TANK WATER HEALTH CHECK</div>
        <div className="mt-1 text-lg font-extrabold">{h.customer_name}</div>
        <div className="mt-1 flex items-start gap-2 text-sm text-slate-200">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0" /><span>{h.property_address}</span>
        </div>
        <div className="mt-2 text-sm font-bold text-white">{h.date_display} · {h.time_display}</div>
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-bold">
          <span className="rounded-full bg-white/10 px-2 py-1" data-testid="field-hc-ref">{h.reference}</span>
          {h.water_source_label && (
            <span className="rounded-full bg-cyan-500/20 px-2 py-1 uppercase text-cyan-100"
              data-testid="field-hc-water-source">{h.water_source_label}</span>
          )}
          <span className="rounded-full bg-white/10 px-2 py-1">
            {h.assigned_technician_name || (h.assigned_technician_id ? "ASSIGNED" : "UNASSIGNED")}
          </span>
          {h.lab?.purchased && <span className="rounded-full bg-cyan-500/20 px-2 py-1 text-cyan-200">{h.lab.test_name}</span>}
          {h.sample_collection_status && (
            <span className="rounded-full bg-white/10 px-2 py-1 uppercase" data-testid="field-hc-collection-state">
              SAMPLE {String(h.sample_collection_status).replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>

      {data.covering && (
        <div className="mt-3 rounded-2xl border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-2"
          data-testid="field-hc-covering-banner">
          <Users className="h-5 w-5 shrink-0 text-amber-700 mt-0.5" />
          <p className="text-sm font-bold text-amber-900">
            You're covering this job{data.covering.assigned_technician_name
              ? ` — originally assigned to ${data.covering.assigned_technician_name}` : " (unassigned)"}.
            The AWARE/ServiceM8 assignment hasn't changed.
          </p>
        </div>
      )}

      {/* AWARE sample collection — reuses the Phase C endpoint */}
      {h.lab?.purchased && (
        <div className={`mt-4 rounded-2xl border-2 p-4 ${h.collect_sample
          ? "border-rose-600 bg-rose-50" : "border-emerald-600 bg-emerald-50"}`}
          data-testid="field-hc-sample-panel">
          <div className="flex items-center gap-2">
            <Beaker className={`h-5 w-5 ${h.collect_sample ? "text-rose-700" : "text-emerald-700"}`} />
            <span className={`text-sm font-extrabold ${h.collect_sample ? "text-rose-800" : "text-emerald-800"}`}>
              AWARE SAMPLE COLLECTION
            </span>
          </div>
          {h.collect_sample ? (
            <>
              <p className="mt-2 text-base font-extrabold text-rose-800" data-testid="field-hc-collect-instruction">
                {h.collection_instruction}
              </p>
              <div className="mt-2 text-sm font-bold text-slate-700">
                {h.lab.test_name} · {h.lab.reference} · TO COLLECT
              </div>
              <button disabled={busy} data-testid="field-hc-mark-collected"
                onClick={() => act(() => api.post(`/field/health-checks/${reference}/collect-sample`,
                  null, { params: coverParams() }), "Sample marked collected")}
                className="mt-3 w-full rounded-full bg-rose-700 px-5 py-4 text-sm font-extrabold text-white">
                MARK SAMPLE COLLECTED
              </button>
              <button data-testid="field-hc-unable-open" onClick={() => setUnableOpen((v) => !v)}
                className="mt-2 w-full rounded-full border border-slate-300 px-5 py-3 text-xs font-bold text-slate-600">
                UNABLE TO COLLECT
              </button>
              {unableOpen && (
                <div className="mt-3 rounded-xl bg-white p-3" data-testid="field-hc-unable-panel">
                  <Chips label="Reason (required)" options={data.schema.unable_reasons}
                    value={unableReason} onPick={setUnableReason} testid="field-hc-unable-reason" />
                  <Notes label="Notes" value={unableNotes} onChange={setUnableNotes} testid="field-hc-unable-notes" />
                  <button disabled={busy || !unableReason} data-testid="field-hc-unable-submit"
                    onClick={() => act(() => api.post(`/field/health-checks/${reference}/unable-to-collect`,
                      { reason: unableReason, notes: unableNotes }, { params: coverParams() }),
                      "Recorded — office will follow up")}
                    className="mt-3 w-full rounded-full bg-slate-900 px-5 py-3 text-sm font-extrabold text-white disabled:opacity-40">
                    RECORD UNABLE TO COLLECT
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-2" data-testid="field-hc-sample-done">
              <p className="text-base font-extrabold text-emerald-800">
                {h.sample_collection_status === "collected" ? "SAMPLE COLLECTED"
                  : `SAMPLE ${String(h.sample_collection_status || "").replace(/_/g, " ").toUpperCase()}`}
              </p>
              <div className="mt-1 text-sm text-slate-700">
                {h.lab.test_name} · {h.lab.reference}
                {h.sample_collected_at && <> · {new Date(h.sample_collected_at).toLocaleString("en-AU")}</>}
                {h.sample_collected_by && <> · {h.sample_collected_by}</>}
                {h.sample_not_collected_reason && <> · {h.sample_not_collected_reason}</>}
              </div>
            </div>
          )}
        </div>
      )}

      <Section title="A · Tank condition" icon={Droplets} testid="field-hc-section-tank"
        open={!!open.tank} onToggle={() => setOpen((o) => ({ ...o, tank: !o.tank }))}
        done={done(["tank.sediment", "tank.water_condition", "tank.lid_access", "tank.overflow_screening"])}>
        <Chips label="Visible sediment" options={S["tank.sediment"]} value={val("tank", "sediment")}
          onPick={(o) => set("tank", "sediment", o)} testid="field-hc-tank-sediment" />
        <Chips label="Tank interior / visible water condition" multi options={M["tank.water_condition"]}
          value={val("tank", "water_condition")} onPick={(o) => toggleMulti("tank", "water_condition", o)}
          testid="field-hc-tank-water" />
        <Chips label="Tank access / lid" options={S["tank.lid_access"]} value={val("tank", "lid_access")}
          onPick={(o) => set("tank", "lid_access", o)} testid="field-hc-tank-lid" />
        <Chips label="Overflow / screening" options={S["tank.overflow_screening"]}
          value={val("tank", "overflow_screening")} onPick={(o) => set("tank", "overflow_screening", o)}
          testid="field-hc-tank-overflow" />
        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Tanks by type
          </div>
          <div className="mt-2 space-y-2">
            {TANK_TYPES.map(([key, label]) => {
              const counts = val("tank", "type_counts") || {};
              const n = Number(counts[key] || 0);
              const bump = (delta) => {
                const next = Math.max(0, Math.min(50, n + delta));
                const merged = { ...counts, [key]: next };
                Object.keys(merged).forEach((k) => { if (!merged[k]) delete merged[k]; });
                setMany("tank", {
                  type_counts: merged,
                  quantity: Object.values(merged).reduce((a, b) => a + Number(b || 0), 0) || "",
                  types: TANK_TYPES.filter(([k]) => merged[k] > 0).map(([, l]) => l),
                });
              };
              return (
                <div key={key} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                  <span className="text-sm font-semibold text-slate-700">{label}</span>
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid={`field-hc-tank-${key}-minus`}
                      onClick={() => bump(-1)} disabled={n === 0}
                      className="h-9 w-9 rounded-full border border-slate-300 text-lg font-bold text-slate-600 disabled:opacity-30">
                      −
                    </button>
                    <span data-testid={`field-hc-tank-${key}-count`}
                      className={`w-6 text-center text-base font-extrabold ${n ? "text-teal-700" : "text-slate-300"}`}>
                      {n}
                    </span>
                    <button type="button" data-testid={`field-hc-tank-${key}-plus`}
                      onClick={() => bump(1)}
                      className="h-9 w-9 rounded-full border border-slate-300 text-lg font-bold text-slate-600">
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-xs text-slate-500" data-testid="field-hc-tank-total">
            Total tanks: <b>{Number(val("tank", "quantity") || 0)}</b>
          </div>
        </div>
        <Notes label="Tank labels (e.g. Tank 1, House Tank)" value={val("tank", "labels")}
          onChange={(v) => set("tank", "labels", v)} testid="field-hc-tank-labels"
          placeholder="Tank 1, Shed Tank" rows={1} />
        <Notes label="Tank condition notes" value={val("tank", "notes")}
          onChange={(v) => set("tank", "notes", v)} testid="field-hc-tank-notes" />
        <Photos category="tank" label="Tank photo" />
        <Photos category="sediment" label="Sediment photo" />
        <Photos category="water_appearance" label="Water appearance photo" />
        <Photos category="overflow" label="Overflow / screening photo" />
      </Section>

      <Section title="B · Filtration & UV" icon={Droplets} testid="field-hc-section-treatment"
        open={!!open.treatment} onToggle={() => setOpen((o) => ({ ...o, treatment: !o.treatment }))}
        done={done(["treatment.filtration_installed", "treatment.uv_installed",
          "treatment.filtration_type", "treatment.filter_condition", "treatment.uv_condition"])}>
        <Chips label="Filtration installed" options={S["treatment.filtration_installed"]}
          value={val("treatment", "filtration_installed")}
          onPick={(o) => set("treatment", "filtration_installed", o)} testid="field-hc-filt-installed" />
        {val("treatment", "filtration_installed") === "Yes" && (
          <>
            <Chips label="System type" options={S["treatment.filtration_type"]}
              value={val("treatment", "filtration_type")}
              onPick={(o) => set("treatment", "filtration_type", o)} testid="field-hc-filt-type" />
            <Chips label="Filter condition" options={S["treatment.filter_condition"]}
              value={val("treatment", "filter_condition")}
              onPick={(o) => set("treatment", "filter_condition", o)} testid="field-hc-filt-condition" />
          </>
        )}
        <Chips label="UV installed" options={S["treatment.uv_installed"]}
          value={val("treatment", "uv_installed")} onPick={(o) => set("treatment", "uv_installed", o)}
          testid="field-hc-uv-installed" />
        {val("treatment", "uv_installed") === "Yes" && (
          <Chips label="UV condition" options={S["treatment.uv_condition"]}
            value={val("treatment", "uv_condition")} onPick={(o) => set("treatment", "uv_condition", o)}
            testid="field-hc-uv-condition" />
        )}
        <Notes label="Filtration / UV notes" value={val("treatment", "notes")}
          onChange={(v) => set("treatment", "notes", v)} testid="field-hc-treatment-notes" />
        <Photos category="filtration" label="Filtration photo" />
        <Photos category="uv" label="UV photo" />
        {(data.photos || []).some((p) => p.category === "filtration_uv") && (
          <Photos category="filtration_uv" label="Filtration / UV photo" />
        )}
      </Section>

      <Section title="C · Owner discussion" icon={User} testid="field-hc-section-owner"
        open={!!open.owner} onToggle={() => setOpen((o) => ({ ...o, owner: !o.owner }))}
        done={done(["owner.owner_present"])}>
        <Chips label="Owner present" options={S["owner.owner_present"]} value={val("owner", "owner_present")}
          onPick={(o) => {
            set("owner", "owner_present", o);
            if (o === "No") {
              set("owner", "findings_shown", false);
              set("owner", "findings_explained", false);
            }
          }} testid="field-hc-owner-present" />
        {val("owner", "owner_present") === "No" && (
          <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm font-bold text-amber-800"
            data-testid="field-hc-owner-absent">
            OWNER NOT PRESENT — record your observations; findings cannot be marked as shown.
          </p>
        )}
        {val("owner", "owner_present") === "Yes" && (
          <div className="mt-4 space-y-3">
            {[["findings_shown", "Owner shown findings"], ["findings_explained", "Findings explained"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 text-base font-bold text-slate-800">
                <input type="checkbox" data-testid={`field-hc-owner-${k}`} className="h-6 w-6"
                  checked={!!val("owner", k)} onChange={(e) => set("owner", k, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        )}
        <Notes label="Owner questions / notes" value={val("owner", "notes")}
          onChange={(v) => set("owner", "notes", v)} testid="field-hc-owner-notes" />
      </Section>

      <Section title="D · Health Check outcome" icon={CheckCircle2} testid="field-hc-section-outcome"
        open={!!open.outcome} onToggle={() => setOpen((o) => ({ ...o, outcome: !o.outcome }))}
        done={done(["outcome.recommendations", "outcome.priority", "outcome.summary", "outcome.other_note"])}>
        <div className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">Recommendations</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {REC_ORDER.map((k) => (
            <button key={k} type="button" data-testid={`field-hc-rec-${k}`} onClick={() => toggleRec(k)}
              className={`rounded-full px-4 py-3 text-sm font-bold ${recs.includes(k)
                ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-700"}`}>
              {data.schema.recommendations[k]?.label || k}
            </button>
          ))}
        </div>
        {recs.includes("other") && (
          <Notes label="Other — note required" value={val("outcome", "other_note")}
            onChange={(v) => set("outcome", "other_note", v)} testid="field-hc-other-note"
            placeholder="What needs following up?" />
        )}
        <Chips label="Priority" options={S["outcome.priority"]} value={val("outcome", "priority")}
          onPick={(o) => set("outcome", "priority", o)} testid="field-hc-priority" />
        <Chips label="Customer interest (optional)" multi options={M["outcome.customer_interest"]}
          value={val("outcome", "customer_interest")}
          onPick={(o) => toggleMulti("outcome", "customer_interest", o)}
          testid="field-hc-customer-interest" />
        <Chips label="Quote preference (optional)" options={S["outcome.quote_preference"]}
          value={val("outcome", "quote_preference")}
          onPick={(o) => set("outcome", "quote_preference", o)} testid="field-hc-quote-preference" />
        <Notes label="Technician summary (internal — never printed in the customer report)"
          rows={4} value={val("outcome", "summary")}
          onChange={(v) => set("outcome", "summary", v)} testid="field-hc-summary"
          placeholder="What you saw and what you told the owner" />
      </Section>

      {completed && data.form?.summary && (
        <div className="mt-4 rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-4" data-testid="field-hc-completed">
          <p className="text-sm font-extrabold text-emerald-800">HEALTH CHECK COMPLETE</p>
          <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
            {(data.form.summary.onsite_findings || []).map((f) => <li key={f}>{f}</li>)}
          </ul>
          {!!(data.follow_ups || []).length && (
            <p className="mt-3 text-xs font-bold text-slate-600" data-testid="field-hc-followups-created">
              {data.follow_ups.length} follow-up action(s) sent to the office
            </p>
          )}
          {!!(data.reports || []).length && (
            <p className="mt-1 text-xs font-bold text-emerald-800" data-testid="field-hc-report-state">
              Customer report v{data.reports[data.reports.length - 1].version}
              {data.reports[data.reports.length - 1].sent_at ? " emailed to the customer" : " generated"}
            </p>
          )}
        </div>
      )}

      {/* sticky action bar */}
      {!completed && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur"
          data-testid="field-hc-actionbar">
          <div className="mx-auto flex max-w-2xl items-center gap-3">
            <div className="flex-1 text-xs font-bold text-slate-500" data-testid="field-hc-save-state">
              {saving ? "SAVING…" : savedAt ? "SAVED" : "AUTOSAVE ON"}
              {!!missing.length && <span className="ml-2 text-amber-700">{missing.length} TO GO</span>}
            </div>
            <button disabled={busy} data-testid="field-hc-complete"
              onClick={() => act(() => api.post(`/field/health-checks/${reference}/complete`,
                null, { params: coverParams() }), "Health Check completed")}
              className="rounded-full bg-cyan-700 px-6 py-4 text-sm font-extrabold text-white disabled:opacity-50">
              COMPLETE HEALTH CHECK
            </button>
          </div>
        </div>
      )}

      {coverModal}
    </div>
  );
}
